/**
 * Animation variants and utilities for Framer Motion
 * 动画变体定义文件 - 用于 Assessments 落地页动画效果
 */

import type { Variants, Transition } from "framer-motion";

// ============================================
// Easing Functions
// ============================================

export const easings = {
  easeOutQuad: [0.25, 0.46, 0.45, 0.94] as const,
  easeOutCubic: [0.33, 1, 0.68, 1] as const,
  easeInOutCubic: [0.65, 0, 0.35, 1] as const,
};

// ============================================
// Fade Up Variants (入场淡入上升动画)
// ============================================

export const fadeUpVariants: Variants = {
  hidden: {
    opacity: 0,
    y: 20,
  },
  visible: (delay: number = 0) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      delay,
      ease: easings.easeOutQuad,
    },
  }),
};

// ============================================
// Stagger Container Variants (交错动画容器)
// ============================================

export const staggerContainerVariants: Variants = {
  hidden: { opacity: 1 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1,
    },
  },
};

// ============================================
// Card Variants (卡片动画)
// ============================================

export const cardVariants: Variants = {
  hidden: {
    opacity: 0,
    y: 30,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: "easeOut",
    },
  },
};

// ============================================
// Count Up Variants (数字计数动画)
// ============================================

export const countUpVariants: Variants = {
  hidden: {
    opacity: 0,
    scale: 0.8,
  },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.5,
      ease: "easeOut",
    },
  },
};

// ============================================
// Line Draw Variants (连接线绘制动画)
// ============================================

export const lineDrawVariants: Variants = {
  hidden: {
    pathLength: 0,
    opacity: 0,
  },
  visible: {
    pathLength: 1,
    opacity: 1,
    transition: {
      duration: 0.8,
      ease: "easeInOut",
    },
  },
};

// ============================================
// Pop In Variants (弹出动画)
// ============================================

export const popInVariants: Variants = {
  hidden: {
    opacity: 0,
    scale: 0,
  },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      type: "spring",
      stiffness: 400,
      damping: 15,
    },
  },
};

// ============================================
// Scale Fade Variants (缩放淡入动画 - CTA按钮)
// ============================================

export const scaleFadeVariants: Variants = {
  hidden: {
    opacity: 0,
    scale: 0.95,
    y: 20,
  },
  visible: (delay: number = 0) => ({
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      duration: 0.5,
      delay,
      ease: easings.easeOutCubic,
    },
  }),
};

// ============================================
// Hover Effects (悬停效果)
// ============================================

export const hoverEffects = {
  lift: {
    y: -4,
    transition: { duration: 0.2 },
  },
  glow: {
    boxShadow: "0 0 20px rgba(255, 255, 255, 0.1)",
    transition: { duration: 0.2 },
  },
  scale: {
    scale: 1.02,
    transition: { duration: 0.2 },
  },
  ctaButton: {
    scale: 1.02,
    boxShadow: "0 0 30px rgba(255, 255, 255, 0.15)",
    transition: { duration: 0.2 },
  },
  iconPulse: {
    scale: 1.1,
    transition: { duration: 0.2 },
  },
} as const;

// ============================================
// Step Animation Variants (步骤流程动画)
// ============================================

export const stepVariants: Variants = {
  hidden: {
    opacity: 0,
    x: -20,
  },
  visible: (index: number = 0) => ({
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.5,
      delay: index * 0.15,
      ease: easings.easeOutQuad,
    },
  }),
};

// ============================================
// Animation Timing Constants
// ============================================

export const ANIMATION_TIMING = {
  // Hero 区域延迟 (ms)
  HERO_TITLE_DELAY: 0,
  HERO_SUBTITLE_DELAY: 0.1,
  HERO_DESCRIPTION_DELAY: 0.2,
  HERO_CTA_DELAY: 0.3,
  HERO_SCREENSHOT_DELAY: 0.4,

  // 通用时长
  FADE_DURATION: 0.6,
  CARD_DURATION: 0.5,
  STAGGER_DELAY: 0.1,

  // 最大动画时长限制 (符合 Requirements 5.4)
  MAX_ENTRANCE_DURATION: 1.0,
} as const;

// ============================================
// GPU-Accelerated Properties Check
// ============================================

/**
 * 验证动画属性是否只使用 GPU 加速属性
 * GPU 加速属性: opacity, transform (scale, rotate, x, y), filter
 */
export const GPU_ACCELERATED_PROPERTIES = [
  "opacity",
  "scale",
  "scaleX",
  "scaleY",
  "rotate",
  "rotateX",
  "rotateY",
  "rotateZ",
  "x",
  "y",
  "z",
  "translateX",
  "translateY",
  "translateZ",
  "filter",
  "pathLength", // SVG 动画
] as const;

export type GPUAcceleratedProperty =
  (typeof GPU_ACCELERATED_PROPERTIES)[number];

/**
 * 检查动画配置是否只使用 GPU 加速属性
 */
export function isGPUAccelerated(properties: string[]): boolean {
  const allowedProps = new Set<string>([
    ...GPU_ACCELERATED_PROPERTIES,
    "transition", // transition 配置不是动画属性
  ]);
  return properties.every((prop) => allowedProps.has(prop));
}

// ============================================
// Reduced Motion Variants (无障碍降级)
// ============================================

export const reducedMotionVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { duration: 0 },
  },
};
